import PublicationTypes from "constants/publication-types";
import { atom, useAtom, useAtomValue } from "jotai";
import PublicationMetadataTypeEnum from "models/interfaces/publication-metadata-types";
import AnnexTableOfContentsRecord from "models/view-models/table-of-contents/annex-table-of-contents-record";
import ArticleTableOfContentsRecord from "models/view-models/table-of-contents/article-table-of-contents-record";
import ChapterTableOfContentsRecord from "models/view-models/table-of-contents/chapter-table-of-contents-record";
import { useOpenTocPanels } from "pages/notebooks/notebook-reference-panel/use-open-toc-panels";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import { TableOfContentsAtom } from "utilities/hooks/domain/publications/use-bookview-table-of-contents";
export const CurrentTableOfContentsItem = atom<TableOfContentsItem>();
export interface TableOfContentsItem {
    id: string | number;
    publicationType: PublicationComponentType | PublicationMetadataTypeEnum;
    parentId?: string;
    parentComponentType?:
        | PublicationComponentType
        | PublicationMetadataTypeEnum;
    nfpaLabel?: string;
    annexId?: string | number;
}

export const ChaptersAtom = atom(
    (get) => get(TableOfContentsAtom)?.chapters ?? []
);
export const AnnexesAtom = atom(
    (get) => get(TableOfContentsAtom)?.annexes ?? []
);

const useVirtualTableOfContents = () => {
    const [currentTocItem, setCurrentTocItem] = useAtom(
        CurrentTableOfContentsItem
    );
    const chapters = useAtomValue(ChaptersAtom);
    const annexes = useAtomValue(AnnexesAtom);
    const { publication } = useCurrentPublication();
    const { openPreviousPanel } = useOpenTocPanels();

    const onNavigationClick = (
        id: number | string | undefined,
        caapsType: PublicationComponentType | PublicationMetadataTypeEnum,
        parentId?: string
    ) => {
        savePanelToReopen(id, caapsType, parentId);
        populateCurrentTocItem(id, caapsType, parentId);
    };

    const savePanelToReopen = (id, caapsType, parentId) => {
        const panelsToReopen: TableOfContentsItem[] = [];

        switch (caapsType) {
            case PublicationComponentType.Annex:
                const annexToReopen = findParentOfSectionOrSelf(
                    annexes,
                    id,
                    caapsType
                );
                panelsToReopen.push({
                    id: `${annexToReopen?.id}`,
                    publicationType: PublicationComponentType.Annex,
                });
                break;
            case PublicationComponentType.Chapter:
                const chapterToReopen = findParentOfSectionOrSelf(
                    chapters,
                    id,
                    caapsType
                );
                panelsToReopen.push({
                    id: `${chapterToReopen?.id}`,
                    publicationType: PublicationComponentType.Chapter,
                });
                break;
            case PublicationComponentType.Article:
                const chapter = findChapterForNECSection(parentId);
                panelsToReopen.push({
                    id: `${chapter?.id}`,
                    publicationType: PublicationComponentType.Chapter,
                });
                break;
            case PublicationComponentType.Section:
                if (publication.type === PublicationTypes.NEC) {
                    const articleToReopen = findArticleForSection(id, parentId);
                    panelsToReopen.push(
                        {
                            id: `${articleToReopen?.id}`,
                            publicationType: PublicationComponentType.Article,
                        },
                        {
                            id: `${articleToReopen?.chapterId}`,
                            publicationType: PublicationComponentType.Chapter,
                        }
                    );
                } else {
                    const chapterToReopen = findParentOfSectionOrSelf(
                        chapters,
                        id,
                        caapsType
                    );
                    panelsToReopen.push({
                        id: `${chapterToReopen?.id}`,
                        publicationType: PublicationComponentType.Chapter,
                    });
                }
        }

        openPreviousPanel(panelsToReopen);
    };

    const populateCurrentTocItem = (id, caapsType, parentId?) => {
        let nfpaLabel: string | undefined;
        switch (caapsType) {
            case PublicationComponentType.Annex:
                nfpaLabel = annexes.find((a) => a.id === id)?.nfpaLabel;
                break;

            case PublicationComponentType.AnnexGroup:
                nfpaLabel = annexes.find(
                    (a) => a.id === Number(parentId)
                )?.nfpaLabel;
                break;

            case PublicationComponentType.Section:
                nfpaLabel = chapters.find(
                    (c) => c.id === Number(parentId)
                )?.nfpaLabel;

                const isChapter9 = nfpaLabel === "9";

                if (publication.type === PublicationTypes.NEC && !isChapter9) {
                    // Show Article if NEC and not NFPA 70 Chapter 9
                    nfpaLabel = findArticleNfpaLabelFromSection(id, parentId);
                }
                break;

            case PublicationComponentType.Article:
                nfpaLabel = findArticleLabel(id);
                break;

            default: {
                // Fallback: Chapter
                nfpaLabel = chapters.find(
                    (c) => c.id === Number(parentId)
                )?.nfpaLabel;
            }
        }

        const tableOfContentsItem = {
            id,
            publicationType: caapsType,
            parentId,
            nfpaLabel,
        };
        setCurrentTocItem(tableOfContentsItem);
    };

    const findChapterForNECSection = (parentId) => {
        const chapter = chapters?.find(
            (chapter) => chapter.id === Number(parentId)
        );
        return chapter;
    };

    const findArticleForSection = (id, parentId) => {
        const chapter = findChapterForNECSection(parentId);
        const article = chapter?.articles?.find((article) =>
            article.sections?.some((s) => s.id === Number(id))
        );
        return article;
    };

    const findArticleNfpaLabelFromSection = (id, parentId) => {
        const article = findArticleForSection(id, parentId);
        return article?.nfpaLabel;
    };

    const findArticleLabel = (articleId) => {
        return chapters
            ?.find((chapter) =>
                chapter.articles?.some((article) => article.id === articleId)
            )
            ?.articles?.find((article) => article.id === articleId)?.nfpaLabel;
    };

    return {
        onNavigationClick,
        currentTocItem,
    };
};

const findParentOfSectionOrSelf = (
    parents:
        | ChapterTableOfContentsRecord[]
        | ArticleTableOfContentsRecord[]
        | AnnexTableOfContentsRecord[],
    caapsItemId: string | number,
    caapsType: PublicationComponentType
) => {
    return caapsType === PublicationComponentType.Chapter
        ? parents.find((parent) => parent?.id === caapsItemId)
        : parents.find((parent) =>
              parent.sections?.some((section) => section.id === caapsItemId)
          );
};

export default useVirtualTableOfContents;
